/**
 * src/components/layout/ApiErrorBanner.jsx
 *
 * App-shell banner shown whenever azureClient's azureGet/azurePost/envFetch
 * throw. This is the central, catch-all error surface: it fires from inside
 * azureClient itself (see surfaceApiError), before the failure reaches any
 * caller's own catch block — so it still tells the user something failed
 * even when that caller's catch only logs and swallows the error. Listens
 * for the 'seasuite:api-error' window event. Dismissible; auto-clears after
 * a few seconds.
 *
 * Most API calls that can fail with a user-visible error (e.g. a 409 from
 * vendors-write) happen while a form modal is open. `.modal-backdrop` is a
 * position:fixed, full-viewport layer at z-index 700 (src/styles/globals.css)
 * — a banner sitting in TopBannerStack's normal document flow at a lower
 * z-index/no position is painted over by that backdrop and never seen, even
 * though its own state and dispatch path are firing correctly. This banner is
 * therefore deliberately taken out of flow (position:fixed) and given a
 * z-index above the modal layer, positioned just below whatever other
 * still-in-flow banners are currently showing via the same
 * --top-banner-stack-height variable DocumentEditor/DocumentSlidePanel use.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const AUTO_DISMISS_MS = 8000;

export default function ApiErrorBanner() {
  const [message, setMessage] = useState(null);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    setMessage(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    const onError = (e) => {
      setMessage(e.detail?.message || 'A request to the server failed.');
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setMessage(null), AUTO_DISMISS_MS);
    };
    window.addEventListener('seasuite:api-error', onError);
    return () => {
      window.removeEventListener('seasuite:api-error', onError);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!message) return null;

  return (
    <div
      role="alert"
      style={{
        position: 'fixed',
        top: 'var(--top-banner-stack-height, 0px)',
        left: 0,
        right: 0,
        background: 'var(--danger-bg, rgba(239,68,68,0.08))',
        borderBottom: '1px solid var(--danger)',
        color: 'var(--danger)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 20px',
        fontSize: '13px',
        fontFamily: 'var(--font-mono)',
        gap: 12,
        // Above .modal-backdrop's z-index:700 (src/styles/globals.css) so a
        // write failure stays visible while a form modal is open — see the
        // file header comment for why this banner alone needs it.
        zIndex: 750,
        pointerEvents: 'auto',
      }}
    >
      <span>
        <span style={{ fontWeight: 600 }}>Request failed</span> — {message}
      </span>
      <button
        onClick={dismiss}
        aria-label="Dismiss error notice"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          fontSize: 18,
          lineHeight: 1,
          padding: '0 4px',
          opacity: 0.7,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
